import { headers } from "@utils/utils";
import { dataTool } from "echarts";

const useBiografiaStore = defineStore("biografia", () => {
  // stados
  const biografia = ref({
    id: null,
    titulo: null,
    sub_titulo: "",
    descripcion: "",
    autor: null,
    estatus: 1, // 1: borrador 2: publicado, 3: archivado
    biografiable_id: null,
    biografiable_type: null,
    biografiable: null,
    entrevista_completada: false,
    porcentaje_entrevista: 0,
    nro_paginas: 1,
    documento: [],
    pagina_dedicatoria: false,
    dedicatoria: null,
    sobre_autor: null,
    pagina_indice: false,
    pagina_autor: false,
    pagina_colaboradores: false,
    pagina_derechos: false,
    en_salon: false,
    completado: false,
    iniciada: true,
  });

  const biografias = ref([]);

  // getters

  const draft = computed(() => clone(biografia.value));
  const entrevistaCompletada = computed(
    () => biografia.value.entrevista_completada
  );
  const getAutor = computed(() => {
    return biografia.value.autor ?? {};
  });

  // mutations

  const clear = () => {
    biografia.value = {
      id: null,
      titulo: null,
      sub_titulo: "",
      descripcion: "",
      autor: null,
      estatus: 1, // 1: borrador 2: publicado, 3: archivado
      biografiable_id: null,
      biografiable_type: null,
      biografiable: null,
      entrevista_completada: false,
      porcentaje_entrevista: 0,
      nro_paginas: 1,
      documento: [],
      pagina_dedicatoria: false,
      dedicatoria: null,
      sobre_autor: null,
      pagina_indice: false,
      pagina_autor: false,
      pagina_colaboradores: false,
      pagina_derechos: false,
      en_salon: false,
      completado: false,
      iniciada: true,
    };
  };

  const actualizarNumeracion = (cant) => {
    biografia.value.nro_paginas = cant;
  };

  const updatePorcentajeEntrevistas = (porcentaje) => {
    if (biografia.value.id) {
      biografia.value.porcentaje_entrevista = porcentaje;
    }
  };

  const setBiografia = (payload) => {
    if (payload) {
      biografia.value = payload;
    }
  };

  const updateContenido = (payload) => {
    if (payload) {
      biografia.value.contenido = payload;
    }
  };

  const setDocumento = (doc) => {
    if (doc) {
      biografia.value.documento = doc;
    }
  };

  const setDedicatoria = (doc) => {
    if (doc) biografia.value.dedicatoria = doc;
  };

  const setSobreAutor = (sa) => {
    if (sa) biografia.value.sobre_autor = sa;
  };

  const completada = (bio_id) => {
    if (bio_id === biografia.value.id) {
      biografia.value.completado = true;
    }
  };

  const descompletada = (bio_id) => {
    if (bio_id === biografia.value.id) {
      biografia.value.completado = false;
    }
  };

  // actions

  const fetch = (tipo_id) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/biografias/${tipo_id}/fetch-data`)
        .then(({ data }) => {
          if (data) {
            biografia.value = data;
          }
          resolve(data);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const fetchData = (datos) => {
    return new Promise((resolve, reject) => {
      axios
        .post(`/api/biografias/fetch-data`, datos)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const previsualizar = (bio_id) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/biografias/${bio_id}/fetch-data-previsualizador`)
        .then(({ data }) => {
          biografia.value = data;
          resolve(data);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const guardar = (datos) => {
    return new Promise((resolve, reject) => {
      if (datos.id) {
        // actualizado
        axios
          .put(`/api/biografias/${datos.id}`, datos)
          .then(({ data }) => {
            if (data.result) {
              biografia.value = data.biografia;
            }
            resolve(data);
          })
          .catch((e) => reject(e));
      } else {
        // creando
        axios
          .post(`/api/biografias`, datos)
          .then(({ data }) => {
            resolve(data);
          })
          .catch((e) => reject(e));
      }
    });
  };

  const eliminar = (tipo_id) => {
    return new Promise((resolve, reject) => {
      axios
        .delete(`/api/biografias/${tipo_id}`)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((e) => reject(e));
    });
  };

  const fetchSelectList = () => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/biografias/select-list`)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const insertarImagenes = (datos) => {
    let formData = new FormData();

    Object.keys(datos).forEach((key) => {
      formData.append(key, datos[key]);
    });
    formData.append("_method", "PUT");

    return new Promise((resolve, reject) => {
      axios
        .post(
          `/api/biografias/${datos.id}/insertar-imagenes`,
          formData,
          headers
        )
        .then(({ data }) => {
          resolve(data);
        })
        .catch((e) => reject(e));
    });
  };

  const agregarPagina = ({ pagina, value }) => {
    return new Promise((resolve, reject) => {
      if (biografia.value.id) {
        axios
          .put(`/api/biografias/${biografia.value.id}/agregar-pagina`, {
            pagina,
            value,
          })
          .then(({ data }) => {
            if (data.result) {
              biografia.value.pagina_dedicatoria = value;
            }

            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      } else {
        reject(new Error("El ID es importante"));
      }
    });
  };

  const getDedicatoria = (bio_id) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/biografias/${bio_id}/get-dedicatoria`)
        .then(({ data }) => {
          if (data.id) {
            setBiografia(data);
          }
          resolve(data);
        })
        .catch((e) => reject(e));
    });
  };

  const getSobreAutor = (bio_id) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/biografias/${bio_id}/get-sobre-autor`)
        .then(({ data }) => {
          if (data.id) {
            setBiografia(data);
          }
          resolve(data);
        })
        .catch((e) => reject(e));
    });
  };

  const actualizarDedicatoria = (bio_id, dedicatoria) => {
    return new Promise((resolve, reject) => {
      axios
        .put(`/api/biografias/${bio_id}/actualizar-dedicatoria`, {
          dedicatoria,
        })
        .then(({ data }) => resolve(data))
        .catch((e) => reject(e));
    });
  };

  const generarPaginaSobreAutor = () => {
    return new Promise((resolve, reject) => {
      axios
        .get(
          `/api/biografias/${biografia.value.id}/regenerar-pagina-sobre-autor`
        )
        .then(({ data }) => {
          if (data.result) {
            setSobreAutor(data.contenido);
          }

          resolve(data);
        })
        .catch((e) => reject(e));
    });
  };

  const fetchPorConfig = (config_id) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/biografias/fetch-por-config/${config_id}`)
        .then(({ data }) => {
          setBiografia(data);
          resolve(data);
        })
        .catch((e) => reject(e));
    });
  };

  const generarPdf = (bio_id) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/biografias/${bio_id}/generar-pdf`)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((e) => reject(e));
    });
  };

  const generarBiografia = (bio_id) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/biografias/${bio_id}/generar-biografia`)
        .then(({ data }) => resolve(data))
        .catch((e) => reject(e));
    });
  };

  const cargarColaboradores = (bio_id) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/biografias/${bio_id}/cargar-colaboradores`)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const cargarEbookImages = (bio_id) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/biografias/${bio_id}/get-ebook-images`)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const publicarSalon = (bio_id) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/biografias/${bio_id}/publicar-salon`)
        .then(({ data }) => {
          if (data.result) {
            setBiografia(data.biografia);
          }
          resolve(data);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const fetchDataInmortales = (params) => {
    return new Promise((resolve, reject) => {
      axios
        .post(`/api/salon-inmortales/fetch-data`, params)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const revertirABorrador = async (bio_id) => {
    try {
      const { data } = await axios.get(
        `/api/biografias/${bio_id}/revertir-a-borrador`
      );
      if (data.result) {
        setBiografia(data.biografia);
      }
      return data;
    } catch (error) {
      throw error;
    }
  };

  const despublicarSalon = async (bio_id) => {
    try {
      const { data } = await axios.get(
        `/api/biografias/${bio_id}/despublicar-salon`
      );
      if (data.result) {
        setBiografia(data.biografia);
      }
      return data;
    } catch (error) {
      throw error;
    }
  };

  const cargarPorcentajeCompletado = async (bio_id, filtro) => {
    try {
      const { data } = await axios.post(
        `/api/biografias/${bio_id}/cargar-porcentaje-completado`,
        { filtro }
      );
      return data;
    } catch (error) {
      throw error;
    }
  };

  return {
    // states
    biografia,
    biografias,
    // getters
    draft,
    entrevistaCompletada,
    getAutor,
    // methods or actions
    fetch,
    fetchData,
    guardar,
    eliminar,
    clear,
    fetchSelectList,
    generarPaginaSobreAutor,
    setBiografia,
    updatePorcentajeEntrevistas,
    updateContenido,
    insertarImagenes,
    setDocumento,
    actualizarNumeracion,
    previsualizar,
    agregarPagina,
    getDedicatoria,
    setDedicatoria,
    actualizarDedicatoria,
    getSobreAutor,
    setSobreAutor,
    fetchPorConfig,
    generarPdf,
    cargarColaboradores,
    generarBiografia,
    cargarEbookImages,
    publicarSalon,
    fetchDataInmortales,
    revertirABorrador,
    despublicarSalon,
    cargarPorcentajeCompletado,
    completada,
    descompletada,
  };
});

export default useBiografiaStore;
